import { DocumentSchema, SchemaNode, InputNode } from '../types';
import SETTING_KEYS from '../../../../constants/setting-keys';
import {
    stripHtml,
    wrapLine,
} from '../../../documents/utils/documentViewUtils';

const checkChildEmpty = (value: any): boolean => {
    if (typeof value === 'object' && value !== null) {
        return Object.keys(value).every((k) => checkChildEmpty(value[k]));
    }
    return value === undefined || value === '' || value === null;
};

export function formatMRIParameters(data: any) {
    // Build implant notes first - these take priority and appear at the top
    const implantNotesResult: any = {};
    if (data.implantNotes && Array.isArray(data.implantNotes)) {
        data.implantNotes.forEach((note: any) => {
            if (note.title && note.note) {
                implantNotesResult[note.title] = note.note;
            }
        });
    }

    // Build standard fields, excluding any keys that exist in implant notes
    const standardFields: any = {
        Precautions:
            data[SETTING_KEYS.addInfo] || data['Unsafe description'] || '',
        'MRI Status': data['MRI Status'] || '',
        'Static Magnetic Field':
            data['Static magnetic field']?.join(', ') ||
            data['Static Magnetic Field Strength'] ||
            '',
        'Spatial Gradient':
            data[SETTING_KEYS.spatialGradient]?.gradients?.[0]?.condition ||
            data['Maximum Spatial Field Gradient'] ||
            '',
        SAR:
            data[SETTING_KEYS.sar]?.length > 0
                ? `${data[SETTING_KEYS.sar][0].condition}` // Assumes only one SAR entry is needed
                : data['SAR'] || '',
        Artefacts:
            data[SETTING_KEYS.artefacts]?.length > 0 &&
            data[SETTING_KEYS.artefacts][0].distance !== undefined
                ? `${data[SETTING_KEYS.artefacts]
                      .map((artefact: any) => {
                          const list = [];
                          if (artefact.distance !== undefined) {
                              list.push(
                                  `<li>Distance: ${artefact.distance}mm</li>`
                              );
                          }
                          if (artefact.pulseSequence !== undefined) {
                              list.push(
                                  `<li>Pulse Sequence: ${artefact.pulseSequence}</li>`
                              );
                          }
                          if (artefact.mrSystem !== undefined) {
                              list.push(
                                  `<li>MR System: ${artefact.mrSystem}T</li>`
                              );
                          }
                          if (artefact.condition !== undefined) {
                              list.push(
                                  `<li>Condition: ${artefact.condition}</li>`
                              );
                          }
                          if (list.length > 0) {
                              return `<ul>${list.join('')}</ul>`;
                          }
                          return '';
                      })
                      .join('')}`
                : data['Image Artifact'] || '',
        'Temperature Rise':
            data[SETTING_KEYS.temp]?.length > 0
                ? `${data[SETTING_KEYS.temp][0].condition}`
                : '',
        'Waiting Period': data[SETTING_KEYS.waitingPeriod]?.immediateScan
            ? 'Immediate Scan'
            : data[SETTING_KEYS.waitingPeriod]?.weeksAfterScan !== undefined
            ? `Patient can be scanned ${
                  data[SETTING_KEYS.waitingPeriod].weeksAfterScan
              } weeks after implantation`
            : '',
        'MRI Architecture':
            data[SETTING_KEYS.mriArc] || data['MR Scanner Type'] || '',
        'Slew Rate': data[SETTING_KEYS.slewRate]
            ? `${data[SETTING_KEYS.slewRate].rate} (${
                  data[SETTING_KEYS.slewRate].condition
              })`
            : data['Maximum Gradient Slew Rate'] || '',
        'B1+rms': data[SETTING_KEYS.brms] || data['B1+rms (μT)'] || '',
        'Coil Type':
            data[SETTING_KEYS.coilType] || data['Receive Coil Type'] || '',
        Preparation: data[SETTING_KEYS.prep] || '',
        Image: data[SETTING_KEYS.image] || '',

        // Active Implant Specific Fields
        Generator: data.generator || '',
        Leads: Array.isArray(data.leads)
            ? data.leads.join(', ')
            : data.leads || '',
        Location: data.location || '',
        Orientation: data.orientation || '',
        'Scan Region': data['Scan Region'] || data['Scan Regions'] || '',
        'Device Configuration': data['Device Configuration'] || '',
        'Operating Mode': data['Operating Mode'] || '',
        'Scan Duration': data['Scan Duration'] || '',
        'Continuous Scan': data['Continuous Scan (min)'] || '',
        Cooldown: data['Cooldown (min)'] || '',
        'RF Excitation': data['RF Excitation'] || '',
        'RF Transmit': data['RF Transmit'] || '',
        'RF Polarization': data['RF Polarization'] || '',
        'B0 Field Orientation': data['B0 Field Orientation'] || '',
        'Scanner Geometry': data['Scanner Geometry'] || '',
        'Tx Coil Allowed': data['Tx Coil Allowed'] || '',
        'Rx Coil Allowed': data['Rx Coil Allowed'] || '',
        'RF Transmit Coil Positioning':
            data['RF Transmit Coil Positioning'] || '',
        'Whole Body SAR': data['SAR Whole-Body (W/kg)'] || '',
        'Head SAR': data['SAR Head (W/kg)'] || '',
        'Partial Body SAR': data['SAR Partial-Body (W/kg)'] || '',
    };

    // Add custom fields, but don't overwrite implant notes
    if (data.customFields && Array.isArray(data.customFields)) {
        data.customFields.forEach((field: any) => {
            if (
                field.heading &&
                field.value &&
                !implantNotesResult[field.heading]
            ) {
                standardFields[field.heading] = field.value;
            }
        });
    }

    // Filter out standard fields that conflict with implant notes
    const filteredStandardFields: any = {};
    for (const key of Object.keys(standardFields)) {
        if (!implantNotesResult[key]) {
            filteredStandardFields[key] = standardFields[key];
        }
    }

    // Return with implant notes first, then standard fields
    return {
        ...implantNotesResult,
        ...filteredStandardFields,
    };
}

function formatRecursive(json: any, depth = 0, parentIndex?: number): any {
    if (!json) return '';
    if (Array.isArray(json)) {
        return `<p>${json
            .map<any>((item) => formatRecursive(item, depth + 1))
            .join(', ')}</p>`;
    }

    const entries = Object.entries(json).filter(
        ([key, value]) => value !== '' && value !== null && !key.includes('_id')
    );

    let index = 0;

    return `<div>${entries
        .map<any>(([key, value]: any) => {
            const normalCaseKey = key;
            if (checkChildEmpty(value)) {
                return '';
            }

            if (typeof value !== 'object') {
                return `<p><strong>${normalCaseKey}</strong>: ${value}</p>`;
            }
            if (depth === 0) {
                index++;
                return `<br><h3><strong>${index} ${normalCaseKey}</strong></h3><br>${formatRecursive(
                    value,
                    depth + 1,
                    index
                )}`;
            }
            if (depth > 0) {
                index++;
                return `${index > 1 ? '<br>' : ''}<h4><strong>${
                    parentIndex ? parentIndex + '.' : ''
                }${index} ${normalCaseKey}</strong></h4><br>${formatRecursive(
                    value,
                    depth + 1
                )}`;
            }
        })
        .join('')}</div>`;
}

export const formatConditionals = (conditionals: any): string => {
    // 1. Transform the raw conditionals to the friendly object format
    // Note: formatMRIParameters expects a single object, not an array.
    // If conditionals is an array (e.g. from multiple versions), we might need to handle it.
    // For now assuming single or we map.

    // Check if conditionals is array (e.g. from previous steps)
    const condArray = Array.isArray(conditionals)
        ? conditionals
        : [conditionals];

    // Process each conditional set and join them
    return condArray
        .map((cond, i) => {
            const friendlyData = formatMRIParameters(cond);
            const html = formatRecursive(friendlyData);
            // Only add header if multiple sets
            return condArray.length > 1
                ? `<h3>Condition Set ${i + 1}</h3>${html}`
                : html;
        })
        .join('<hr/>');
};

export const generateTextReport = (
    schema: DocumentSchema,
    data: any
): string => {
    let reportText = '';

    // Helper to get value safely
    const getValue = (key: string) => data[key];

    const normalizeCptCode = (value: any): string | null => {
        const code = typeof value === 'string' ? value : value?.code;
        if (code === '76014' || code === '14') return '76014';
        if (code === '76015' || code === '15') return '76015';
        if (code === '76016' || code === '16') return '76016';
        return null;
    };

    // Helper to format value
    const formatValue = (value: any, fieldId?: string): string => {
        if (value === undefined || value === null || value === '') return '';
        if (Array.isArray(value)) {
            // Check if it's a simple array or array of objects (repeater)
            if (value.length > 0 && typeof value[0] === 'object') {
                // Special handling for implants - only show name/model
                if (fieldId === 'implants') {
                    return value
                        .map((item: any) => {
                            const name =
                                item._implantModel ||
                                item.implantName ||
                                'Unknown Implant';
                            return name;
                        })
                        .join('\n\t\t');
                }
                return value
                    .map((item: any) => {
                        return Object.entries(item)
                            .filter(
                                ([k, v]) =>
                                    v !== '' &&
                                    v !== undefined &&
                                    k !== 'id' &&
                                    k !== '_id' &&
                                    !k.startsWith('_')
                            )
                            .map(([_, v]) => String(v))
                            .join(' - ');
                    })
                    .join('\n\t\t');
            }
            return value.join(', ');
        }
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        // Handle dates if string is ISO?
        // Assuming raw value is fine for now, or Date formatting if needed.
        return String(value);
    };

    schema.sections.forEach((section) => {
        // Skip sections that are purely for actions (buttons)
        if (section.sectionType === 'actions') return;

        // Check if section is effectively empty/hidden?
        // For now, print section title
        let sectionContent = '';
        let hasContent = false;

        sectionContent += `${section.title}\n`;

        section.fields.forEach((field) => {
            // Skip hidden fields if data is missing (simple heuristic)
            // Or if field is kind='action' or 'view'
            if (field.kind === 'action') {
                // For actions, only show checkboxes that are checked (attestations)
                if (field.actionType === 'checkbox') {
                    const val = getValue(field.id);
                    if (val) {
                        sectionContent += `\t[x] ${field.title}\n`;
                        hasContent = true;
                    }
                }
                return;
            }

            if (field.kind === 'view') {
                // Views might be summary calculations.
                if (field.viewType === 'total-summary') {
                    // Summary is derived from task list, skip as it's redundant
                    return;
                }
                if (field.viewType === 'cpt-codes') {
                    // Include CPT codes from saved data
                    const cptCodes = getValue('cptCodes');
                    if (
                        cptCodes &&
                        Array.isArray(cptCodes) &&
                        cptCodes.length > 0
                    ) {
                        const normalizedCodes = cptCodes
                            .map((c: any) => normalizeCptCode(c))
                            .filter(Boolean);
                        const code14Count = normalizedCodes.filter(
                            (code: string | null) => code === '76014'
                        ).length;
                        const code15Count = normalizedCodes.filter(
                            (code: string | null) => code === '76015'
                        ).length;
                        const codesList = cptCodes
                            .map((c: any) => c.label)
                            .join(', ');
                        let summary = '';
                        if (code14Count > 0)
                            summary += `${code14Count}× CPT 76014`;
                        if (code14Count > 0 && code15Count > 0)
                            summary += ' + ';
                        if (code15Count > 0)
                            summary += `${code15Count}× CPT 76015`;
                        sectionContent += `\tApplicable CPT Codes: ${codesList} (${summary})\n`;
                        hasContent = true;
                    }
                    return;
                }
                return;
            }

            if (field.kind === 'input') {
                let value = getValue(field.id);

                if (
                    field.inputType === 'richtext' &&
                    typeof value === 'string'
                ) {
                    value = stripHtml(value);
                }

                if (value !== undefined && value !== null && value !== '') {
                    const formatted = formatValue(value, field.id);

                    // Apply line wrapping to all content
                    // Account for indentation: \t\t = ~8 chars, so wrap at ~92
                    const wrappedFormatted = formatted
                        .split('\n')
                        .map((line) => wrapLine(line, 92))
                        .join('\n');

                    // Check if content is multiline (either originally or after wrapping)
                    const isMultiline =
                        field.inputType === 'richtext' ||
                        field.inputType === 'textarea' ||
                        wrappedFormatted.includes('\n');

                    if (isMultiline) {
                        sectionContent += `\t${field.title}:\n`;
                        const indented = wrappedFormatted
                            .split('\n')
                            .map((line) => `\t\t${line}`)
                            .join('\n');
                        sectionContent += `${indented}\n`;
                    } else {
                        // For single line, check if label + value exceeds limit
                        const singleLine = `\t${field.title}: ${wrappedFormatted}`;
                        if (singleLine.length > 80) {
                            // If too long, format as multiline
                            sectionContent += `\t${field.title}:\n`;
                            const wrapped = wrapLine(wrappedFormatted, 92);
                            const indented = wrapped
                                .split('\n')
                                .map((line) => `\t\t${line}`)
                                .join('\n');
                            sectionContent += `${indented}\n`;
                        } else {
                            sectionContent += `${singleLine}\n`;
                        }
                    }
                    hasContent = true;
                }
            }
        });

        if (hasContent) {
            reportText += sectionContent + '\n';
        }
    });

    return reportText.trim();
};
